import { TextInput, Alert } from '@mgmresorts/mgm-ui';
import dayjs from 'dayjs';
import { navigate } from 'hookrouter';
import qs from 'query-string';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { KnownError } from '../../../api/errors';
import { MlifeVerificationRequest, mlifeVerificationRequest } from '../../../api/fetch/mlife-activation';
import { Container } from '../../../components/ui/Container';
import { BirthdayInput } from '../../../components/ui/Inputs/BirthdayInput';
import { Logo } from '../../../components/ui/Logo';
import { PageLayout } from '../../../components/ui/PageLayout';
import { useBirthdayInput } from '../../../hooks/useBirthdayInput';
import { useContent } from '../../../hooks/useContent';
import { useFieldValidation } from '../../../hooks/useFieldValidation';
import { useInput } from '../../../hooks/useInput';
import { captureEvent, captureException } from '../../../monitoring/logrocket';
import { useEvent } from '../../../utils/analytics/useEvent';
import { MaxLengths } from '../../../utils/constants';
import { PersistentParametersContext } from '../../../utils/persistParameterContext';
import { UserAccountContext } from '../../../utils/userAccountContext';
import { birthdayValidator, emailValidator, mlifeNumberValidator } from '../../../utils/validators';
import {
  StyledAlertContainer,
  StyledButton,
  StyledContent,
  StyledHeading,
  StyledInputContainer,
  StyledLink,
} from '../MlifeActivation.styles';
import { MlifeActivationContext } from '../MlifeActivationContext';

export function MlifeActivation() {
  const { email: userEmail } = useContext(UserAccountContext);
  const { email: emailAddress, mlifeNumber: mlifeNumberValue } = qs.parse(window.location.search) as {
    email: string;
    mlifeNumber: string;
  };
  if ((!emailAddress || !mlifeNumberValue) && !userEmail) {
    navigate('/', true);
  }
  const currentEmail = emailAddress || userEmail || '';
  const trackEvent = useEvent();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setErrorState] = useState<null | string>(null);
  const [trackedErrors, setTrackedErrors] = useState<string[]>([]);
  const { setActivationRequest, setStep } = useContext(MlifeActivationContext);
  const { getContentByPath } = useContent();
  const { validateField, invalidFields } = useFieldValidation([
    emailValidator,
    mlifeNumberValidator,
    birthdayValidator,
  ]);
  const { bind: bindEmail, value: email } = useInput(currentEmail, 'email', undefined, validateField);
  const { bind: bindBirthday, value: birthday } = useBirthdayInput('', 'birthday', undefined, validateField);
  const { bind: bindMlife, value: mlifeNumber } = useInput(
    mlifeNumberValue || '',
    'mlifeNumber',
    undefined,
    (field, value) => validateField(field, value, false),
  );

  const { params } = useContext(PersistentParametersContext);

  const fieldError = useCallback(
    (field: string): string | undefined =>
      invalidFields.includes(field) ? getContentByPath(`mlifeActivation.fields[${field}].error`) : undefined,
    [getContentByPath, invalidFields],
  );

  const fieldBirthdayError = useCallback(
    (field: string): string | undefined =>
      invalidFields.includes(field)
        ? getContentByPath(
            `mlifeActivation.fields[${field}].${bindBirthday?.value?.replace('MM/DD/YYYY', '') ? 'error' : 'empty'}`,
          )
        : undefined,
    [getContentByPath, invalidFields, bindBirthday],
  );

  const canProcessMlifeActivation = useMemo(() => {
    return !!(email && birthday && !invalidFields.length);
  }, [email, birthday, invalidFields.length]);

  const requestMlifeActivation = useCallback(async () => {
    try {
      setIsLoading(true);

      const partnerData =
        params.partner && params.tokenKey
          ? { name: params.partner as string, tokenKey: params.tokenKey as string }
          : undefined;

      const verificationRequestBody: MlifeVerificationRequest = {
        login: email,
        dob: dayjs(birthday).format('YYYY-MM-DD'),
        partner: partnerData,
      };

      captureEvent('Activation', {
        email,
        partner: String(params.partner),
      });

      if (mlifeNumber) {
        verificationRequestBody.mlifeNumber = mlifeNumber;
      }

      const { activationKid } = await mlifeVerificationRequest(verificationRequestBody);

      setActivationRequest({
        login: email,
        birthday: dayjs(birthday).format('YYYY-MM-DD'),
        mlifeNumber,
        activationKid,
      });

      setErrorState(null);
      setIsLoading(false);

      await trackEvent(
        'identity – activate m life - credentials submit success',
        'activate m life credentials submit success',
        'amlifesubmitsuccess',
        {
          activateState: 'success',
          mlifenumberInput: mlifeNumber ? 'true' : 'false',
        },
        true,
      );

      setErrorState('message');

      setStep('verify');
    } catch (err) {
      setIsLoading(false);
      let message: string;
      if (err instanceof KnownError) {
        message = err.message;
      } else {
        message = getContentByPath('mlifeActivation.errors.default');
      }

      setErrorState(message);
      await trackEvent(
        'identity – activate m life - credentials submit error',
        'activate m life credentials submit error',
        'mlifesubmitfailure',
        {
          activateState: 'failed',
          errorDisplayed: message,
          mlifenumberInput: 'true',
        },
        true,
      );
      captureException(err as Error, 'activate', {
        username: email,
        mlifeNumber,
        step: 'verification-request',
      });
    }
  }, [
    params.partner,
    params.tokenKey,
    email,
    birthday,
    mlifeNumber,
    setActivationRequest,
    trackEvent,
    setStep,
    getContentByPath,
  ]);

  useEffect(() => {
    ['email', 'mlifeNumber', 'birthday'].forEach(field => {
      if (invalidFields.includes(field) && !trackedErrors.includes(field)) {
        trackEvent(
          'identity – activate m life - credentials input error',
          'activate m life credentials input error',
          'amlifeinputerror',
          {
            errorDisplayed: getContentByPath(`mlifeActivation.fields[${field}].error`),
            errorField: getContentByPath(`mlifeActivation.fields[${field}].title`),
          },
        );
      }
    });
    setTrackedErrors(invalidFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getContentByPath, invalidFields, trackEvent]);

  return (
    <StyledContent>
      <PageLayout
        testId='route-/activate'
        title={getContentByPath('mlifeActivation.title')}
        description={getContentByPath('mlifeActivation.description')}
      >
        <Container height='auto'>
          <Logo />

          <StyledHeading variant='heading-medium-3' testId='page-title'>
            {getContentByPath('mlifeActivation.title')}
          </StyledHeading>

          {error && (
            <StyledAlertContainer>
              <Alert open variant='error' message={error} testId='mlifeActivation-error' hiddenCloseButton />
            </StyledAlertContainer>
          )}

          <TextInput
            variant='default'
            label={getContentByPath('mlifeActivation.fields.email.title')}
            id='email'
            testId='email'
            icon='lock'
            disabled
            className={`identity__field identity__field-email ${fieldError('email') ? 'with-error' : ''}`}
            error={!!fieldError('email')}
            hint={fieldError('email')}
            {...bindEmail}
            maxLength={MaxLengths.Email}
            autoComplete='email'
            type='email'
          />

          <StyledInputContainer>
            <BirthdayInput
              label={getContentByPath('mlifeActivation.fields.birthday.title')}
              id='birthDay'
              testId='birthDay'
              disabled={isLoading}
              hint={fieldBirthdayError('birthday') || getContentByPath('mlifeActivation.fields.birthday.hint')}
              className={`identity__field identity__field-birthday ${
                fieldBirthdayError('birthday') ? 'with-error' : ''
              }`}
              error={!!fieldBirthdayError('birthday')}
              {...bindBirthday}
            />
          </StyledInputContainer>

          <StyledInputContainer>
            <TextInput
              variant='default'
              label={getContentByPath('mlifeActivation.fields.mlifeNumber.title')}
              id='mlifeNumber'
              testId='mlifeNumber'
              disabled={isLoading}
              error={!!fieldError('mlifeNumber')}
              hint={fieldError('mlifeNumber')}
              className={`identity__field identity__field-mlifenumber ${fieldError('mlifeNumber') ? 'with-error' : ''}`}
              {...bindMlife}
              inputMode='numeric'
            />
          </StyledInputContainer>

          <StyledButton
            size='large'
            variant='primary'
            testId='activate-button'
            label={getContentByPath('mlifeActivation.ctaSection.title')}
            onClick={requestMlifeActivation}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                requestMlifeActivation();
              }
            }}
            className='identity__form-submit'
            type='submit'
            disabled={!canProcessMlifeActivation}
            loading={isLoading}
            fullWidth
          />

          <StyledLink
            label={getContentByPath('mlifeActivation.ctaSection.backToSignIn')}
            size='large'
            variant='tertiary'
            icon='arrow-left'
            fullWidth
            onClick={async () => {
              await trackEvent(
                'identity – m life activation - back to sign in',
                'back to sign in',
                'mlabacktosignin',
                {},
                true,
              );
              navigate('/');
            }}
            onKeyDown={async e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                await trackEvent(
                  'identity – m life activation - back to sign in',
                  'back to sign in',
                  'mlabacktosignin',
                  {},
                  true,
                );
                navigate('/');
              }
            }}
            testId='mlifeActivation-back-link'
          />
        </Container>
      </PageLayout>
    </StyledContent>
  );
}

MlifeActivation.displayName = 'MlifeActivation';
